import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { eur, isApprovedCompany, b2bNetPrice } from "@/lib/api";
import { Plus, Heart, Lock } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { red: "Rosso", white: "Bianco", rose: "Rosato", sparkling: "Bollicine", distillati: "Distillato", box: "Box Regalo" };

export default function WineCard({ wine, index = 0 }) {
  const { add } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const saved = has(wine.id);
  const soldOut = wine.available === false || Number(wine.stock ?? 0) <= 0;
  const onPromo = wine.original_price && Number(wine.original_price) > Number(wine.price);
  const promoLocked = onPromo && !user;
  const handleAdd = (e) => {
    e.preventDefault();
    if (promoLocked) {
      toast.error("Accedi o registrati per acquistare i prodotti in promozione");
      navigate("/login");
      return;
    }
    add(wine);
    toast.success(`${wine.name} aggiunto`);
  };
  const discountPct = onPromo
    ? Math.ceil((Number(wine.original_price) - Number(wine.price)) / Number(wine.original_price) * 100)
    : 0;
  const isBox = wine.type === "box";
  const FORMAT_LABEL = { magnum: "Magnum 1,5L", "375": "37,5cl", "500": "50cl", "1L": "1L" };
  const formatBadge = FORMAT_LABEL[wine.format];
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`wine-card-${wine.id}`}
      className="group"
    >
      <Link to={`/wine/${wine.id}`} className="block">
        <div className={`img-zoom relative aspect-[3/4] bg-linen ${isBox ? "ring-2 ring-claret ring-offset-2 ring-offset-bone" : ""}`}>
          <img src={wine.image} alt={wine.name} loading="lazy" decoding="async" width="600" height="800" className={`w-full h-full object-cover transition-[filter] duration-500 ${soldOut ? "grayscale opacity-70" : ""}`} />
          {soldOut ? (
            <span data-testid={`sold-out-${wine.id}`} className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-ink text-bone px-3 py-1">Esaurito</span>
          ) : (
            <span className={`absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-3 py-1 ${isBox ? "bg-claret text-bone" : "bg-bone/90 backdrop-blur"}`}>{TYPE_LABEL[wine.type] || wine.type}</span>
          )}
          {formatBadge && !soldOut && (
            <span data-testid={`format-badge-${wine.id}`} className="absolute top-12 left-4 text-[10px] uppercase tracking-[0.15em] bg-ink/90 text-bone px-3 py-1">{formatBadge}</span>
          )}
          {onPromo && !soldOut && (
            <span data-testid={`promo-badge-${wine.id}`} className="absolute top-14 right-4 text-[10px] uppercase tracking-[0.15em] bg-claret text-bone px-3 py-1 font-600">Promo</span>
          )}
          {onPromo && !soldOut && discountPct > 0 && (
            <span data-testid={`discount-badge-${wine.id}`} className="absolute top-[5.5rem] right-4 text-[10px] uppercase tracking-[0.15em] bg-ink text-bone px-3 py-1 font-600">-{discountPct}%</span>
          )}
          <button
            data-testid={`wishlist-toggle-${wine.id}`}
            aria-label={saved ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
            onClick={(e) => { e.preventDefault(); toggle(wine.id); toast.success(saved ? "Rimosso dai preferiti" : "Aggiunto ai preferiti"); }}
            className={`absolute top-4 right-4 w-9 h-9 flex items-center justify-center backdrop-blur transition-colors ${saved ? "bg-claret text-bone" : "bg-bone/85 text-ink hover:bg-claret hover:text-bone"}`}
          >
            <Heart size={16} strokeWidth={1.8} fill={saved ? "currentColor" : "none"} />
          </button>
          {!soldOut && wine.stock > 0 && wine.stock <= 5 && (
            <span data-testid={`low-stock-${wine.id}`} className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.1em] bg-claret text-bone px-3 py-1 font-600 animate-pulse">{wine.stock === 1 ? "Ultima rimasta" : `Solo ${wine.stock} rimaste`}</span>
          )}
          {!soldOut && (
            <button
              data-testid={`quick-add-${wine.id}`}
              onClick={handleAdd}
              className="absolute bottom-4 right-4 w-11 h-11 bg-ink text-bone flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-[opacity,transform] duration-500 hover:bg-claret"
              aria-label={promoLocked ? "Accedi per acquistare la promozione" : "Aggiungi al carrello"}
              title={promoLocked ? "Accedi per acquistare i prodotti in promozione" : "Aggiungi al carrello"}
            >
              {promoLocked ? <Lock size={16} /> : <Plus size={18} />}
            </button>
          )}
        </div>
      </Link>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/wine/${wine.id}`}>
            <h3 className={`font-serif-display text-xl leading-tight break-normal [overflow-wrap:normal] transition-colors ${soldOut ? "text-ink-soft" : "group-hover:text-claret"}`}>{wine.name}</h3>
          </Link>
          <p className="text-xs uppercase tracking-wide text-ink-soft mt-1 break-normal">{wine.producer} · {wine.region}</p>
        </div>
        <div className="flex flex-col items-end whitespace-nowrap shrink-0">
          {isApprovedCompany(user) ? (
            <>
              <span className="text-[10px] uppercase tracking-wide text-claret">Prezzo azienda</span>
              <span className={`text-lg ${soldOut ? "text-ink-soft line-through" : "text-claret"}`} data-testid={`b2b-price-${wine.id}`}>
                {eur(b2bNetPrice(wine.price, user.discount_percent))} <span className="text-[11px] text-ink-soft normal-case tracking-normal">iva escl.</span>
              </span>
              <span className="text-[11px] text-ink-soft">{eur(wine.price)} al pubblico</span>
            </>
          ) : (
            <>
              {onPromo && !soldOut && <span className="text-xs text-ink-soft line-through" data-testid={`original-price-${wine.id}`}>{eur(wine.original_price)}</span>}
              <span className={`text-lg ${soldOut ? "text-ink-soft line-through" : (onPromo ? "text-claret" : "")}`}>{eur(wine.price)}</span>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
